import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { motion } from "framer-motion";
import { Download } from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const COVER = (slug) => `${process.env.REACT_APP_BACKEND_URL}/api/files/dahmani-food/categories/${slug}.webp`;

const CATEGORIES = [
  { n: 1, slug: "spezie", name: "Spezie & Aromi", img: COVER("spezie"), alt: "Spezie e aromi" },
  { n: 2, slug: "bibite", name: "Bibite & Succhi", img: COVER("bibite"), alt: "Bibite e succhi" },
  { n: 3, slug: "olive", name: "Olive & Conserve", img: COVER("olive"), alt: "Olive e conserve" },
  { n: 4, slug: "salumi", name: "Salumi Tipici", img: COVER("salumi"), alt: "Salumi tipici" },
  { n: 5, slug: "tisane", name: "Tisane & Infusi", img: COVER("tisane"), alt: "Tisane e infusi" },
  { n: 6, slug: "olio", name: "Olio Extravergine", img: COVER("olio"), alt: "Olio extravergine" },
  { n: 7, slug: "frutta-secca", name: "Frutta Secca", img: COVER("frutta-secca"), alt: "Frutta secca" },
  { n: 8, slug: "couscous", name: "Couscous & Semola", img: COVER("couscous"), alt: "Couscous e semola" },
  { n: 9, slug: "salse", name: "Salse & Harissa", img: COVER("salse"), alt: "Salse e harissa" },
  { n: 10, slug: "freschi", name: "Prodotti Freschi", img: COVER("freschi"), alt: "Prodotti freschi" },
];

const EXTRA = [
  { n: 11, slug: "farina", name: "Farine & Lieviti", img: COVER("farina"), alt: "Farine, semole e lieviti" },
  { n: 12, slug: "dadi", name: "Dadi & Preparati", img: COVER("dadi"), alt: "Dadi da brodo e preparati" },
  { n: 13, slug: "tonno", name: "Tonno & Ittico", img: COVER("tonno"), alt: "Tonno e conserve ittiche" },
  { n: 14, slug: "legumi", name: "Legumi Secchi", img: COVER("legumi"), alt: "Legumi secchi" },
  { n: 15, slug: "bazar", name: "Articoli Bazar", img: COVER("bazar"), alt: "Articoli bazar e casalinghi" },
];

export default function Categories() {
  const navigate = useNavigate();
  const [products, setProducts] = useState([]);
  const [apiCategories, setApiCategories] = useState([]);

  useEffect(() => {
    axios.get(`${API}/products`)
      .then((r) => { setProducts(r.data.products); setApiCategories(r.data.categories); })
      .catch(() => {});
  }, []);

  const counts = useMemo(() => {
    const m = {};
    products.forEach((p) => { m[p.category] = (m[p.category] || 0) + 1; });
    return m;
  }, [products]);

  const knownSlugs = new Set([...CATEGORIES, ...EXTRA].map((c) => c.slug));
  const extraDynamic = apiCategories
    .filter((c) => !knownSlugs.has(c.slug))
    .map((c, i) => ({ n: 16 + i, slug: c.slug, name: c.label, img: COVER(c.slug), alt: c.label }));

  const openDialog = (slug) => navigate(`/prodotti/${slug}`);

  const downloadCatalog = async () => {
    const win = window.open("", "_blank");
    try {
      const url = `${API}/catalog.pdf?v=${Date.now()}`;
      const res = await fetch(url, { method: "HEAD" });
      if (res.ok && win) {
        win.location.href = url;
        return;
      }
      if (win) win.close();
      toast.info("Catalogo in preparazione", { description: "Il PDF del catalogo sarà disponibile a breve. Richiedilo tramite il form contatti." });
    } catch {
      if (win) win.close();
      toast.info("Catalogo in preparazione", { description: "Il PDF del catalogo sarà disponibile a breve. Richiedilo tramite il form contatti." });
    }
  };

  return (
    <section id="categorie" data-testid="categories-section" className="py-24 md:py-32 bg-[#faf8f5]">
      <div className="max-w-6xl mx-auto px-6 space-y-16">
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 border-b border-[#0b2545]/15 pb-8">
          <div className="space-y-3">
            <span className="text-xs uppercase font-mono-df tracking-widest text-[#c59b27] font-semibold">Assortimento Completo</span>
            <h2 className="font-display text-3xl sm:text-4xl md:text-5xl text-[#0b2545] font-normal">I Nostri Reparti Specializzati</h2>
          </div>
          <div className="text-xs text-[#0b2545]/70 max-w-xs">
            {products.length > 0 ? `${products.length} referenze` : "Prodotti scelti"} per negozi, gastronomie e canali Ho.Re.Ca. Tocca una categoria per vedere le referenze.
          </div>
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4 md:gap-6">
          {[...CATEGORIES, ...EXTRA, ...extraDynamic].map((cat, i) => (
            <motion.button
              key={cat.slug}
              type="button"
              onClick={() => openDialog(cat.slug)}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-40px" }}
              transition={{ duration: 0.6, delay: (i % 5) * 0.08, ease: [0.2, 0.7, 0, 1] }}
              data-testid={`category-card-${cat.n}`}
              className="group relative rounded-2xl overflow-hidden aspect-square border border-[#0b2545]/10 shadow-sm text-left cursor-pointer"
            >
              <img src={cat.img} alt={cat.alt} loading="lazy" className="absolute inset-0 w-full h-full object-cover df-drift" />
              <div className="absolute inset-0 p-4 flex flex-col justify-between bg-gradient-to-t from-[#06152d]/90 via-transparent to-transparent text-white">
                <div className="flex items-start justify-between">
                  <span className="text-[10px] font-mono-df uppercase text-[#d4af37] bg-[#06152d]/60 px-1.5 py-0.5 rounded">{String(cat.n).padStart(2, "0")}</span>
                  {counts[cat.slug] > 0 && (
                    <span className="text-[10px] font-mono-df bg-[#d4af37]/90 text-[#06152d] px-2 py-0.5 rounded-full">{counts[cat.slug]}</span>
                  )}
                </div>
                <span className="font-display text-base font-semibold group-hover:text-[#d4af37] transition-colors">{cat.name}</span>
              </div>
            </motion.button>
          ))}
        </div>

        <div className="p-8 md:p-12 rounded-3xl bg-gradient-to-r from-[#0b2545] to-[#06152d] text-white flex flex-col md:flex-row items-center justify-between gap-6 border border-[#d4af37]/30">
          <div className="space-y-2 text-center md:text-left">
            <h3 className="font-display text-2xl md:text-3xl font-medium">Catalogo Completo Dahmani Food</h3>
            <p className="text-xs text-white/70 max-w-md">Consulta l'elenco dettagliato delle referenze con formati e packaging per la distribuzione.</p>
          </div>
          <button
            data-testid="download-catalog-btn"
            onClick={downloadCatalog}
            className="px-6 py-3.5 rounded-full bg-[#d4af37] text-[#06152d] text-xs font-bold uppercase tracking-wider hover:bg-white transition-colors flex items-center gap-2"
          >
            <Download className="w-4 h-4" />
            <span>Scarica Catalogo PDF</span>
          </button>
        </div>
      </div>
    </section>
  );
}
